import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  loginUserService,
  verifyOtpService,
  forgotPasswordService,
  resetPasswordService,
} from "../services/loginServices";
import { jwtDecode } from "jwt-decode";

// Define the initial state
const initialState = {
  api_token: localStorage.getItem("authToken") || null,
  status: "idle",
  otpStatus: "idle",
  forgotPasswordStatus: "idle",
  resetPasswordStatus: "idle",
  emailVerify: null,
  emailForOtp: null,
  error: null,
  otpError: null,
  forgotPasswordError: null,
  resetPasswordError: null,
  resetPasswordMessage: null, // Add this for success message
};

// Define async thunk for login request
export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const response = await loginUserService(email, password);
      return { ...response.data.data, email }; // Return email for OTP
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to login");
    }
  }
);

// Define async thunk for OTP verification
export const verifyOtp = createAsyncThunk(
  "auth/verifyOtp",
  async ({ email, otp }, { rejectWithValue }) => {
    try {
      const response = await verifyOtpService(email, otp, "login"); // Call OTP verification service
      return response.data.data; // Return data upon successful OTP verification
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to verify OTP");
    }
  }
);

// Define async thunk for forgot password request
export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await forgotPasswordService(email); // Call forgot password service
      return response.data.message; // Return success message
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to send forgot password request"
      );
    }
  }
);

// Define async thunk for reset password request
export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async ({ email, password }, { rejectWithValue }) => {
    if (!email) {
      return rejectWithValue("Email is required for reset password");
    }

    try {
      const response = await resetPasswordService(email, password);
      return response.data.message;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to reset password"
      );
    }
  }
);

const loginSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      state.api_token = null;
      state.status = "idle";
      state.otpStatus = "idle";
      state.forgotPasswordStatus = "idle";
      state.resetPasswordStatus = "idle"; // Reset password status on logout
      state.error = null;
      state.otpError = null;
      state.forgotPasswordError = null;
      state.resetPasswordError = null;
      localStorage.removeItem("authToken");
      localStorage.removeItem("tokenExpiry");
    },
    resetAuthState: (state) => {
      state.status = "idle";
      state.otpStatus = "idle";
      state.forgotPasswordStatus = "idle";
      state.resetPasswordStatus = "idle";
      state.error = null;
      state.otpError = null;
      state.forgotPasswordError = null;
      state.resetPasswordError = null;
      state.resetPasswordMessage = null; // Reset success message
      state.emailForOtp = null; // Reset email after the flow is complete
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle loginUser cases
      .addCase(loginUser.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        const { api_token, email_verify, email } = action.payload;

        state.emailVerify = email_verify;
        state.emailForOtp = email;

        // Email not verified → do NOT store token, but trigger OTP modal
        if (!email_verify) {
          state.status = "succeeded"; // UI sees this as success and opens OTP modal
          state.api_token = null; // ensure not logged in
          return;
        }

        // Verified → normal login
        state.status = "succeeded";
        state.api_token = api_token;

        localStorage.setItem("authToken", api_token);

        try {
          const decodedToken = jwtDecode(api_token);
          if (decodedToken.exp) {
            localStorage.setItem("tokenExpiry", decodedToken.exp * 1000);
          }
        } catch (error) {
          console.error("Error decoding token:", error);
        }
      })

      .addCase(loginUser.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || "Failed to login";
      })

      // Handle verifyOtp cases
      .addCase(verifyOtp.pending, (state) => {
        state.otpStatus = "loading";
        state.otpError = null;
      })
      .addCase(verifyOtp.fulfilled, (state) => {
        state.otpStatus = "success";
        state.otpError = null;
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.otpStatus = "failed";
        state.otpError = action.payload?.message || "Failed to verify OTP";
      })

      // Handle forgotPassword cases
      .addCase(forgotPassword.pending, (state) => {
        state.forgotPasswordStatus = "loading";
        state.forgotPasswordError = null;
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.forgotPasswordStatus = "success";
        const email = action.meta.arg.email;
        state.emailForOtp = email; // Store the email correctly
        state.forgotPasswordError = null;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.forgotPasswordStatus = "failed";
        state.forgotPasswordError =
          action.payload?.message || "Failed to send forgot password request";
      })

      // Handle resetPassword cases
      .addCase(resetPassword.pending, (state) => {
        state.resetPasswordStatus = "loading";
        state.resetPasswordError = null;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.resetPasswordStatus = "success";
        state.resetPasswordMessage = action.payload; // Store success message
        state.resetPasswordError = null;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.resetPasswordStatus = "failed";

        // Handle possible API formats:
        const payload = action.payload;

        state.resetPasswordError =
          payload?.message ||
          payload?.error ||
          payload?.msg ||
          typeof payload === "string"
            ? payload
            : "Failed to reset password";
      });
  },
});

// Export actions and reducer
export const { logout, resetAuthState } = loginSlice.actions;
export default loginSlice.reducer;
